<template>
  <div
    ref="rootRef"
    v-resize:300="onResize"
    :class="classes"
    class="sc-list"
    role="tablist"
    @focusin="onFocusin"
    @focusout="onFocusout"
  >
    <q-icon
      class="sc-list--arrow sc-list--arrow--prev sc-item--icon"
      :class="{ 'sc-list--arrow--faded':!leftArrow }"
      :name="prevIcon || vertical ? 'mono-chevron-up' : 'mono-chevron-left'"
      @mousedown.passive="scrollToStart"
      @mouseup.passive="stopAnimScroll"
      @mouseleave.passive="stopAnimScroll"
      @touchstart.passive="scrollToStart"
      @touchend.passive="stopAnimScroll"
    />
    <div
      ref="contentRef"
      class="sc-list--content"
      :class="innerClass"
      @scroll="updateArrows"
    >
      <!-- <div
        v-if="indicatorVisible"
        class="sc-item--indicator"
        :class="tabProps.indicatorClass"
        :style="indicatorStyle"
      /> -->
      <slot />
      <!-- sliding active-tab indicator -->
    </div>
    <q-icon
      class="sc-list--arrow sc-list--arrow--next sc-item--icon"
      :class="{ 'sc-list--arrow--faded': !rightArrow }"
      :name="nextIcon || vertical ? 'mono-chevron-down' : 'mono-chevron-right'"
      @mousedown.passive="scrollToEnd"
      @mouseup.passive="stopAnimScroll"
      @mouseleave.passive="stopAnimScroll"
      @touchstart.passive="scrollToEnd"
      @touchend.passive="stopAnimScroll"
    />
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick, provide, onActivated, onDeactivated, onMounted, onBeforeUnmount } from 'vue'
import { useRoute } from 'vue-router'
import { useQuasar } from 'quasar'
import { cx } from '@/helpers'

defineOptions({ name: 'SegmentedControl' })
const emit = defineEmits(['update:modelValue', 'resize'])
const currentModel = defineModel({ type: [String, Number], required: true })
const props = defineProps({
  modelValue: { type: [Number, String], default: null },
  align: { type: String, default: 'center', validator: v => ['left', 'center', 'right', 'justify'].includes(v) },
  breakpoint: { type: [String, Number], default: 600 },
  vertical: { type: Boolean, default: false },
  shrink: { type: Boolean, default: false },
  stretch: { type: Boolean, default: false },
  activeClass: { type: String, default: null },
  activeColor: { type: String, default: null },
  activeBgColor: { type: String, default: null },
  indicatorColor: { type: String, default: null },
  indicatorShape: { type: String, default: 'rounded', validator: v => ['square', 'rounded'].includes(v) },
  shape: { type: String, default: 'square', validator: v => ['square', 'round'].includes(v) },
  prevIcon: { type: String, default: null },
  nextIcon: { type: String, default: null },
  outsideArrows: { type: Boolean, default: false },
  mobileArrows: { type: Boolean, default: false },
  inlineLabel: { type: Boolean, default: false },
  noCaps: { type: Boolean, default: false },
  dense: { type: Boolean, default: false },
  contentClass: { type: String, default: null },
  flat: { type: Boolean, default: false }
  // 'onUpdate:modelValue': { type: [Function, Array], default: null }
})

const $q = useQuasar()
let $route = null
try {
  $route = useRoute()
// eslint-disable-next-line no-empty
} catch (_) {}

// Tick helpers
// Latest-wins coalescing: if the same slot is registered again before the tick
// fires, only the most recently registered callback runs.
const scrollTickFn = ref(null)
const updateArrowsTickFn = ref(null)
const animateTickFn = ref(null)

const registerTick = (fn, tickFn) => {
  tickFn.value = fn
  nextTick(() => {
    if (tickFn.value === fn) {
      tickFn.value()
      tickFn.value = null
    }
  })
}

// Timeout helpers─
const focusTimer = ref(null)
const scrollToTabTimer = ref(null)

const removeTimeout = timer => {
  if (timer.value !== null) {
    clearTimeout(timer.value)
    timer.value = null
  }
}
const registerTimeout = (fn, delay, timer) => {
  removeTimeout(timer)
  timer.value = setTimeout(() => {
    timer.value = null
    fn()
  }, delay)
}

// DOM refs
const rootRef = ref(null)
const contentRef = ref(null)

// State
const scrollable = ref(false)
const leftArrow = ref(true)
const rightArrow = ref(false)
const justify = ref(false)
const tabDataListLen = ref(0) // ref so hasActiveTab computed reacts to tab registration
const hasFocus = ref(false)
const indicatorVisible = ref(false)
const indicatorStyle = ref({})

// Plain vars — no reactivity needed; managed via explicit calls
const tabDataList = []
let animateTimer = null
let scrollTimer = null
let hadActivated = false
let unwatchRoute = undefined // unwatch fn; set when a RouteSegment registers
let hadRouteWatcher = false // keeps-alive: re-establish route watcher on reactivation

// RTL scroll bug: some old browsers return positive scrollLeft in RTL instead of negative.
// Modern browsers don't have this bug. Replace false with runtime detection if needed.
// Ref: quasar/src/utils/private.rtl/rtl.js
const rtlHasScrollBug = false

// Computed
const tabProps = computed(() => {
  return {
    activeClass: props.activeClass,
    activeColor: props.activeColor,
    activeBgColor: props.activeBgColor,
    indicatorClass: cx(
      props.indicatorColor && `text-${props.indicatorColor}`,
      `shape-${props.indicatorShape}`
    ),
    inlineLabel: props.inlineLabel,
    noCaps: props.noCaps
  }
})

// Manual loop (not .some) so tabDataListLen ref correctly drives reactivity
const hasActiveTab = computed(() => {
  const len = tabDataListLen.value
  const val = currentModel.value
  for (let i = 0; i < len; i++) {
    if (tabDataList[i].name.value === val) return true
  }
  return false
})

const classes = computed(() => cx(
  `sc-list--${props.vertical ? 'vertical' : 'horizontal'}`,
  scrollable.value ? 'is-scrollable' : 'not-scrollable',
  `arrows--${props.outsideArrows ? 'outside' : 'inside'}`,
  `is-mobile-with${props.mobileArrows ? '' : 'out'}-arrows`,
  `shape-${props.shape}`,
  props.dense && 'is-dense',
  props.shrink && 'is-shrink',
  props.stretch && 'is-stretch',
  props.flat && 'is-flat'
))

const innerClass = computed(() => cx(
  'scroll--mobile',
  `is-align-${scrollable.value ? 'left' : (justify.value ? 'justify' : props.align)}`,
  props.contentClass
))

const domProps = computed(() => (
  props.vertical
    ? { container: 'height', content: 'offsetHeight', scroll: 'scrollHeight' }
    : { container: 'width', content: 'offsetWidth', scroll: 'scrollWidth' }
))

const isRTL = computed(() => props.vertical !== true && $q.lang.rtl === true)
const rtlPosCorrection = computed(() => rtlHasScrollBug === false && isRTL.value === true)

// Abstracted scroll-position accessors — computed once, called in the hot scrollTowards() loop
const posFn = computed(() => (
  rtlPosCorrection.value === true
    ? {
        get: el => Math.abs(el.scrollLeft),
        set: (el, pos) => {
          el.scrollLeft = -pos
        }
      }
    : props.vertical === true
      ? {
          get: el => el.scrollTop,
          set: (el, pos) => {
            el.scrollTop = pos
          }
        }
      : {
          get: el => el.scrollLeft,
          set: (el, pos) => {
            el.scrollLeft = pos
          }
        }
))

// Indicator
// Custom sliding indicator that tracks the active tab's bounding box.
// The per-tab FLIP animation (in animate()) uses each Segment's own tabIndicatorRef.
const updateIndicator = () => {
  const activeTab = tabDataList.find(t => t.name.value === currentModel.value)
  if (!activeTab || !contentRef.value) {
    indicatorVisible.value = false
    return
  }
  const el = activeTab.rootRef.value
  if (!el) return

  const rect = el.getBoundingClientRect()
  const parentRect = contentRef.value.getBoundingClientRect()

  indicatorStyle.value = props.vertical
    ? { height: rect.height + 'px', top: rect.top - parentRect.top + 'px' }
    : { width: rect.width + 'px', left: rect.left - parentRect.left + 'px' }

  indicatorVisible.value = true
}
const scheduleIndicatorUpdate = () => requestAnimationFrame(updateIndicator)

// Model
const updateModel = ({ name, setCurrent, skipEmit }) => {
  if (currentModel.value === name) return
  if (skipEmit !== true && props['onUpdate:modelValue']) emit('update:modelValue', name)
  if (setCurrent === true || !props['onUpdate:modelValue']) {
    animate(currentModel.value, name)
    currentModel.value = name
  }
}

// Animation───────
const animate = (oldName, newName) => {
  const oldTab = oldName != null ? tabDataList.find(tab => tab.name.value === oldName) : null
  const newTab = newName != null ? tabDataList.find(tab => tab.name.value === newName) : null

  // FLIP animation on the per-tab indicator elements.
  // Skip after keep-alive reactivation — treat it like a fresh mount.
  if (!hadActivated && oldTab?.tabIndicatorRef?.value && newTab?.tabIndicatorRef?.value) {
    const oldEl = oldTab.tabIndicatorRef.value
    const newEl = newTab.tabIndicatorRef.value

    if (animateTimer !== null) {
      clearTimeout(animateTimer)
      animateTimer = null
    }

    oldEl.style.transition = 'none'
    oldEl.style.transform = 'none'
    newEl.style.transition = 'none'
    newEl.style.transform = 'none'

    const oldPos = oldEl.getBoundingClientRect()
    const newPos = newEl.getBoundingClientRect()

    newEl.style.transform = props.vertical === true
      ? `translate3d(0,${oldPos.top - newPos.top}px,0) scale3d(1,${newPos.height ? oldPos.height / newPos.height : 1},1)`
      : `translate3d(${oldPos.left - newPos.left}px,0,0) scale3d(${newPos.width ? oldPos.width / newPos.width : 1},1,1)`

    // Allow scope updates to settle before transitioning back
    registerTick(() => {
      animateTimer = setTimeout(() => {
        animateTimer = null
        newEl.style.transition = 'transform .25s cubic-bezier(.4, 0, .2, 1)'
        newEl.style.transform = 'none'
      }, 70)
    }, animateTickFn)
  }

  hadActivated = false

  if (newTab && scrollable.value === true) {
    scrollToTabEl(newTab.rootRef.value)
  }

  scheduleIndicatorUpdate()
}

// Scroll / layout─
const updateContainer = domSize => {
  if (!domProps.value || contentRef.value === null) return

  const size = domSize[domProps.value.container]
  const scrollSize = Math.min(
    contentRef.value[domProps.value.scroll],
    [...contentRef.value.children].reduce((sum, el) => sum + (el[domProps.value.content] || 0), 0)
  )
  // Chrome reports size=0 / scrollSize=1 when there are no tabs — treat as non-scrollable
  const scroll = size > 0 && scrollSize > size

  scrollable.value = scroll
  if (scroll) registerTick(updateArrows, updateArrowsTickFn)
  justify.value = size < parseInt(props.breakpoint, 10)
}

const recalculateScroll = () => {
  registerTick(() => {
    if (!rootRef.value) return
    updateContainer({
      width: rootRef.value.offsetWidth,
      height: rootRef.value.offsetHeight
    })
  }, scrollTickFn)
}

const scrollToTabEl = el => {
  if (!el || !contentRef.value) return

  const { left, width, top, height } = contentRef.value.getBoundingClientRect()
  const newPos = el.getBoundingClientRect()
  let offset = props.vertical ? newPos.top - top : newPos.left - left

  if (offset < 0) {
    contentRef.value[props.vertical ? 'scrollTop' : 'scrollLeft'] += Math.floor(offset)
    updateArrows()
    return
  }

  offset += props.vertical ? newPos.height - height : newPos.width - width
  if (offset > 0) {
    contentRef.value[props.vertical ? 'scrollTop' : 'scrollLeft'] += Math.ceil(offset)
    updateArrows()
  }
}

const updateArrows = () => {
  const content = contentRef.value
  if (!content) return

  const rect = content.getBoundingClientRect()
  const pos = props.vertical ? content.scrollTop : Math.abs(content.scrollLeft)

  if (isRTL.value === true) {
    leftArrow.value = Math.ceil(pos + rect.width) < content.scrollWidth - 1
    rightArrow.value = pos > 0
  } else {
    leftArrow.value = pos > 0
    rightArrow.value = props.vertical
      ? Math.ceil(pos + rect.height) < content.scrollHeight
      : Math.ceil(pos + rect.width) < content.scrollWidth
  }
}

const scrollTowards = val => {
  const { get, set } = posFn.value
  const content = contentRef.value
  let done = false
  let pos = get(content)

  const direction = val < pos ? -1 : 1
  pos += direction * 5

  if (pos < 0) {
    done = true
    pos = 0
  } else if ((direction === -1 && pos <= val) || (direction === 1 && pos >= val)) {
    done = true
    pos = val
  }

  set(content, pos)
  updateArrows()
  return done
}

const animScrollTo = value => {
  if (scrollTimer !== null) clearInterval(scrollTimer)
  scrollTimer = setInterval(() => {
    if (scrollTowards(value) === true) stopAnimScroll()
  }, 5)
}

const scrollToStart = () => animScrollTo(rtlPosCorrection.value ? Number.MAX_SAFE_INTEGER : 0)
const scrollToEnd = () => animScrollTo(rtlPosCorrection.value ? 0 : Number.MAX_SAFE_INTEGER)
const stopAnimScroll = () => {
  if (scrollTimer !== null) {
    clearInterval(scrollTimer)
    scrollTimer = null
  }
}

// Keyboard navigation
const onKbdNavigate = (key, fromEl) => {
  // Only include focusable (non-disabled) tabs in the navigation ring
  const tabs = Array.prototype.filter.call(
    contentRef.value.children,
    el => el === fromEl || (el.matches && el.matches('.sc-item.is-focusable') === true)
  )
  const len = tabs.length
  if (!len) return false

  if (key === 'Home') {
    scrollToTabEl(tabs[0])
    tabs[0].focus()
    return true
  }
  if (key === 'End') {
    scrollToTabEl(tabs[len - 1])
    tabs[len - 1].focus()
    return true
  }

  const dirPrev = key === (props.vertical ? 'ArrowUp' : 'ArrowLeft')
  const dirNext = key === (props.vertical ? 'ArrowDown' : 'ArrowRight')
  const dir = dirPrev ? -1 : dirNext ? 1 : 0
  if (dir === 0) return false

  const rtlDir = isRTL.value ? -1 : 1
  const index = tabs.indexOf(fromEl) + dir * rtlDir

  if (index >= 0 && index < len) {
    scrollToTabEl(tabs[index])
    tabs[index].focus({ preventScroll: true })
    return true
  }
  return false
}

// Focus management
const onFocusin = e => {
  removeTimeout(focusTimer)
  if (
    hasFocus.value !== true
    && rootRef.value !== null
    && e.target
    && typeof e.target.closest === 'function'
  ) {
    const tab = e.target.closest('.sc-item')
    if (tab && rootRef.value.contains(tab) === true) {
      hasFocus.value = true
      if (scrollable.value === true) scrollToTabEl(tab)
    }
  }
}
const onFocusout = () => registerTimeout(() => hasFocus.value = false, 30, focusTimer)

// Routing─────────
// Returns true if every key in targetQuery exists with the same value in matchingQuery
const hasQueryIncluded = (targetQuery, matchingQuery) => {
  for (const key in targetQuery) {
    if (targetQuery[key] !== matchingQuery[key]) return false
  }
  return true
}

// Finds the best-matching RouteSegment for the current route and updates the model.
// Called via verifyRouteModel; must always reset hadActivated before returning.
const updateActiveRoute = () => {
  if (!$route) return
  let name = null
  let bestScore = { matchedLen: 0, queryDiff: 9999, hrefLen: 0 }

  const list = tabDataList.filter(tab => tab.routeData?.hasRouterLink.value === true)
  const { hash: currentHash, query: currentQuery } = $route
  const currentQueryLen = Object.keys(currentQuery).length

  for (const tab of list) {
    const exact = tab.routeData.exact.value === true
    const isActive = tab.routeData[exact ? 'linkIsExactActive' : 'linkIsActive'].value

    if (isActive !== true) continue

    const { hash, query, matched, href } = tab.routeData.resolvedLink.value
    const queryLen = Object.keys(query).length

    if (exact === true) {
      if (hash !== currentHash) continue
      if (queryLen !== currentQueryLen || !hasQueryIncluded(currentQuery, query)) continue
      name = tab.name.value
      break
    }

    if (hash !== '' && hash !== currentHash) continue
    if (queryLen !== 0 && !hasQueryIncluded(query, currentQuery)) continue

    const newScore = {
      matchedLen: matched.length,
      queryDiff: currentQueryLen - queryLen,
      hrefLen: href.length - hash.length
    }

    if (newScore.matchedLen > bestScore.matchedLen) {
      name = tab.name.value
      bestScore = newScore
      continue
    } else if (newScore.matchedLen !== bestScore.matchedLen) {
      continue
    }

    if (newScore.queryDiff < bestScore.queryDiff) {
      name = tab.name.value
      bestScore = newScore
    } else if (newScore.queryDiff !== bestScore.queryDiff) {
      continue
    }

    if (newScore.hrefLen > bestScore.hrefLen) {
      name = tab.name.value
      bestScore = newScore
    }
  }

  // Don't interfere if a non-route tab is currently active
  if (name === null && tabDataList.some(tab => !tab.routeData && tab.name.value === currentModel.value)) {
    hadActivated = false
    return
  }

  updateModel({ name, setCurrent: true })
}

const verifyRouteModel = () => {
  if ($tabs.avoidRouteWatcher === false) {
    registerTimeout(updateActiveRoute, 0, scrollToTabTimer)
  } else {
    removeTimeout(scrollToTabTimer)
  }
}

const watchRoute = () => {
  if (unwatchRoute !== undefined || !$route) return
  const unwatch = watch(() => $route.fullPath, verifyRouteModel)
  unwatchRoute = () => {
    unwatch()
    unwatchRoute = undefined
  }
}

// Tab registration
const registerTab = tabData => {
  if (tabDataList.includes(tabData)) return
  tabDataList.push(tabData)
  tabDataListLen.value++
  recalculateScroll()

  if (!tabData.routeData) {
    // Plain Segment: scroll to the active tab once layout has settled
    registerTimeout(() => {
      if (scrollable.value !== true) return
      const val = currentModel.value
      if (val == null || val === '') return
      const active = tabDataList.find(tab => tab.name.value === val)
      if (active) scrollToTabEl(active.rootRef.value)
    }, 0, scrollToTabTimer)
  } else {
    // RouteSegment: start watching the route (idempotent)
    watchRoute()
    if (tabData.routeData.hasRouterLink.value === true) {
      verifyRouteModel()
    }
  }

  scheduleIndicatorUpdate()
}

const unregisterTab = tabData => {
  const index = tabDataList.indexOf(tabData)
  if (index === -1) return
  tabDataList.splice(index, 1)
  tabDataListLen.value--
  recalculateScroll()

  if (unwatchRoute !== undefined && tabData.routeData !== undefined) {
    // Stop watching the route if no RouteSegments remain
    if (tabDataList.every(tab => !tab.routeData)) {
      unwatchRoute()
    }
    verifyRouteModel()
  }

  scheduleIndicatorUpdate()
}

const onResize = size => {
  updateContainer(size)
  scheduleIndicatorUpdate()
  emit('resize', size)
}

// Cleanup─────────
const cleanup = () => {
  if (animateTimer !== null) {
    clearTimeout(animateTimer)
    animateTimer = null
  }
  stopAnimScroll()
  unwatchRoute?.()
  scrollTickFn.value = null
  updateArrowsTickFn.value = null
  animateTickFn.value = null
  removeTimeout(focusTimer)
  removeTimeout(scrollToTabTimer)
}

// Watchers────────
watch(isRTL, updateArrows)

// External model change: animate + update locally, do not re-emit to parent
watch(() => props.modelValue, name => {
  updateModel({ name, setCurrent: true, skipEmit: true })
})

watch(() => props.outsideArrows, recalculateScroll)

if (import.meta.env.NODE_ENV === 'development') {
  watch(currentModel, val => {
    if (val != null && !tabDataList.some(t => t.name.value === val)) {
      console.warn(`[SegmentedControl] No <Segment> matches model value: "${val}"`)
    }
  })
}

// Lifecycle───────
onMounted(scheduleIndicatorUpdate)
onBeforeUnmount(cleanup)
onDeactivated(() => {
  hadRouteWatcher = unwatchRoute !== undefined
  cleanup()
})
onActivated(() => {
  hadActivated = true // suppress FLIP animation on first render after reactivation
  if (hadRouteWatcher) {
    watchRoute()
    verifyRouteModel()
  }
  recalculateScroll()
  scheduleIndicatorUpdate()
})

// Provide─────────
const $tabs = {
  currentModel,
  tabProps,
  hasFocus,
  hasActiveTab,
  registerTab,
  unregisterTab,
  updateModel,
  onKbdNavigate,
  verifyRouteModel,
  avoidRouteWatcher: false
}

provide('segmentedTabs', $tabs)
</script>

<style scoped>
@reference "#app-styles.css";

.sc-list {
  --sc-label-font-size: 14px;
  --sc-icon-font-size: 18px;
  --sc-icon-font-width: var(--sc-icon-font-size);
  --sc-icon-font-height: var(--sc-icon-font-size);
  --sc-alert-icon-font-size: 18px;
  --sc-arrow-font-size: 32px;

  @apply relative flex flex-nowrap items-center p-2;
  @apply transition-basic;

  &.shape-square { @apply rounded-md; }
  &.shape-round { @apply rounded-full; }

  &:not(.is-flat) { @apply layer-surf border edge-surf; }
  &.is-stretch { @apply self-center; }
  &.is-shrink { @apply shrink; }
  &.is-scrollable {
    &.arrows--outside {
      @apply px-6;

      &.sc-list--vertical { @apply py-6 px-0; }
      &.sc-list--arrow--faded { @apply opacity-30 pointer-events-none; }
    }
    &.arrows--inside {
      &.sc-list--arrow--faded { @apply hidden; }
    }
  }
  &.not-scrollable.arrows--outside,
  &.is-scrollable.is-mobile-without-arrows.arrows--outside {
    @apply px-0;
  }
  &.not-scrollable .sc-list--arrow { @apply hidden; }
}

@media (hover: none) and (pointer: coarse) {
  .sc-list.is-scrollable.is-mobile-without-arrows .sc-list--arrow {
    @apply hidden;
  }
}
.sc-list.not-scrollable .sc-list--arrow,
.sc-list.is-scrollable.is-mobile-without-arrows .sc-list--content {
  @apply border-inherit;
}
.sc-list--arrow {
  @apply text-(--sc-arrow-font-size) min-w-[calc(var(--sc-arrow-font-size)+4px)];
  @apply cursor-pointer transition-basic;
}
.sc-list--content {
  @apply relative overflow-hidden flex flex-nowrap items-center flex-1 self-stretch;
  @apply gap-2;
  scrollbar-width: none;
  -ms-overflow-style: none;
}
.sc-list--content.is-align-center {
  @apply justify-center;
}
.sc-list--content.is-align-right {
  @apply justify-end;
}
.sc-list--content.is-align-justify :deep(.sc-item) {
  @apply flex-1 justify-center;
}
.sc-list--offset {
  @apply hidden;
}
.sc-list--horizontal .sc-list--arrow {
  @apply h-full;
}
.sc-list--horizontal .sc-list--arrow--prev {
  @apply top-0 left-0 bottom-0;
}
.sc-list--horizontal .sc-list--arrow--next {
  @apply top-0 right-0 bottom-0;
}
.sc-list--vertical {
  @apply block! h-full;
}
.sc-list--vertical .sc-list--content {
  @apply flex-col h-full;
}
.sc-list--vertical .sc-list--vertical {
  @apply w-full h-6 text-center;
}
.sc-list--vertical .sc-list--vertical--arrow--prev {
  @apply top-0 left-0 right-0;
}
.sc-list--vertical .sc-list--vertical--arrow--next {
  @apply bottom-0 left-0 right-0;
}
.sc-list--vertical :deep(.sc-item) {
  @apply w-full;
}
.sc-list--vertical.sc-list--not-scrollable .sc-list--content {
  @apply h-full;
}
.sc-list--vertical.is-dense .sc-item--content {
  @apply min-w-6;
}
.sc-list.is-dense {
  @apply p-1.5;
}
.sc-list.is-dense .sc-list--content {
  @apply gap-1;
}
.sc-list.is-dense :deep(.sc-item) {
  @apply min-h-6.5 px-2;
}
.sc-list.is-dense :deep(.sc-item--content) {
  @apply gap-1;
}
.sc-list.is-dense :deep(.sc-item--full) {
  @apply min-h-8;
}

/* ── Segment / RouteSegment shared styles ───────────────────────────────────── */
/* Kept here (not in Segment.vue) so RouteSegment picks them up via the slot.   */

:deep(.sc-item) {
  @apply relative flex flex-nowrap items-center self-center;
  @apply no-underline text-center py-0 px-4 min-h-10 transition-basic whitespace-nowrap;
}
:deep(.sc-item.is-focusable),
:deep(.sc-item.is-hoverable) {
  @apply outline-0 cursor-pointer;
}
:deep(.sc-item--focus-helper) {
  @apply absolute top-0 left-0 w-full h-full rounded-[inherit];
  @apply opacity-0 transition-basic cursor-pointer;
  @apply before:content-[''] before:absolute before:top-0 before:left-0 before:bg-(--q-background-mid);
  @apply before:h-full before:w-full before:opacity-0 before:rounded-[inherit] before:transition-basic;
  @apply after:content-[''] after:absolute after:top-0 after:left-0 after:bg-(--q-background-inverse);
  @apply after:h-full after:w-full after:opacity-0 after:rounded-[inherit] after:transition-basic;
}
:deep(.sc-item.is-hoverable.is-inactive:hover) {
  @apply ink-strong;
}
:deep(.sc-item.is-focusable:focus > .sc-item--focus-helper),
:deep(.sc-item.is-hoverable:hover > .sc-item--focus-helper) {
  @apply opacity-15;
  @apply before:opacity-1;
  @apply after:opacity-4;
}
:deep(.sc-item.is-focusable:focus > .sc-item--focus-helper) {
  @apply opacity-22;
}
:deep(.sc-item.is-disabled) {
  @apply ink-disabled! outline-0! cursor-not-allowed;
}
:deep(.sc-item.is-full) {
  @apply min-h-10;
}
/* Icon-only segments (no visible label) are forced square so the active
   indicator reads as a circle instead of a pill. */
:deep(.sc-item:not(:has(.sc-item--label))) {
  @apply w-10 h-10 min-w-10 px-0 justify-center shrink-0 rounded-full;
}
.sc-list.is-dense :deep(.sc-item:not(:has(.sc-item--label))) {
  @apply w-6.5 h-6.5 min-w-6.5;
}
:deep(.sc-item--content) {
  @apply relative flex items-center justify-center self-stretch h-[inherit] py-1 px-0 min-w-0;
  @apply select-none!;
}
:deep(.sc-item--content.is-inline) {
  @apply flex flex-nowrap gap-2;
}
:deep(.sc-item--content.is-stack) {
  @apply flex-col;
}
:deep(.sc-item--content .q-chip--floating) {
  @apply top-0 -right-4;
}
:deep(.sc-item--icon) {
  @apply text-(--sc-icon-font-size) w-(--sc-icon-font-size) h-(--sc-icon-font-size);
}
:deep(.sc-item--label) {
  @apply text-(length:--sc-label-font-size) leading-(--sc-label-font-size) font-medium;
}
:deep(.sc-item--alert),
:deep(.sc-item--alert-icon) {
  @apply absolute;
}
:deep(.sc-item--alert) {
  @apply top-2 -right-2 h-2.5 w-2.5 rounded-full bg-current;
}
:deep(.sc-item--alert-icon) {
  @apply text-(length:--sc-alert-icon-font-size) top-0.5 -right-3;
}
:deep(.sc-item--indicator) {
  @apply absolute inset-0 opacity-0 z-0;
  @apply layer-highlight-deep;
  @apply transition-basic;
}
:deep(.sc-item--indicator.shape-square) {
  @apply rounded-md;
}
:deep(.sc-item--indicator.shape-rounded) {
  @apply rounded-full;
}
:deep(.sc-item.is-hoverable.is-inactive:hover .sc-item--indicator) {
  @apply layer-highlight-faint opacity-100;
}
:deep(.sc-item.is-active .sc-item--indicator) {
  @apply opacity-100 origin-left;
}
:deep(.sc-item.is-active) {
  @apply ink-strong;
}
:deep(.sc-item.is-inactive) {
  @apply ink-subtle;
}
</style>
